import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ShieldCheck } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { TablePaginationBar } from '@/components/app/TablePaginationBar'
import { cn } from '@/lib/utils'
import type { ActivityLog } from '@/types/settings'

const actionTypeTone: Record<string, 'success' | 'warning' | 'active' | 'danger' | 'neutral'> = {
  login: 'success',
  logout: 'neutral',
  session_expired: 'warning',
  password_changed: 'success',
  email_change_requested: 'active',
  email_changed: 'success',
  email_verification_resent: 'active',
  name_updated: 'success',
  phone_updated: 'success',
  avatar_updated: 'success',
  business_updated: 'success',
  business_iacrm_updated: 'success',
  business_iacrm_tested: 'success',
  own_iacrm_updated: 'success',
  own_iacrm_tested: 'success',
}

function actionToneClass(tone: string) {
  switch (tone) {
    case 'success':
      return 'border-transparent bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300'
    case 'warning':
      return 'border-transparent bg-amber-500/10 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300'
    case 'active':
      return 'border-transparent bg-sky-500/10 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300'
    case 'danger':
      return 'border-transparent bg-rose-500/10 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300'
    default:
      return 'border-transparent bg-muted text-muted-foreground'
  }
}

function formatDateTime(iso: string | null | undefined, locale: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-FR' : 'en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(d)
}

function simplifyUserAgent(
  ua: string | null | undefined,
  labels: { unknownBrowser: string; unknownDevice: string },
): string {
  if (!ua) return '—'
  const lower = ua.toLowerCase()

  let browser = labels.unknownBrowser
  if (lower.includes('chrome') && !lower.includes('edg')) browser = 'Chrome'
  else if (lower.includes('safari') && !lower.includes('chrome')) browser = 'Safari'
  else if (lower.includes('firefox')) browser = 'Firefox'
  else if (lower.includes('edg')) browser = 'Edge'
  else if (lower.includes('opera') || lower.includes('opr')) browser = 'Opera'

  let device = labels.unknownDevice
  if (lower.includes('iphone')) device = 'iPhone'
  else if (lower.includes('ipad')) device = 'iPad'
  else if (lower.includes('android')) device = 'Android'
  else if (lower.includes('macintosh') || lower.includes('mac os')) device = 'Mac'
  else if (lower.includes('windows')) device = 'Windows'
  else if (lower.includes('linux')) device = 'Linux'

  return `${browser} · ${device}`
}

interface SecuritySessionsTableProps {
  logs: ActivityLog[] | undefined
  isLoading: boolean
}

export function SecuritySessionsTable({ logs, isLoading }: SecuritySessionsTableProps) {
  const { t, i18n } = useTranslation()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const rows = useMemo(() => {
    if (!logs) return []
    return [...logs].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    )
  }, [logs])

  const totalItems = rows.length
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize))
  const pageSafe = Math.min(page, totalPages)

  const pageSlice = useMemo(() => {
    const start = (pageSafe - 1) * pageSize
    return rows.slice(start, start + pageSize)
  }, [rows, pageSafe, pageSize])

  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-lg border border-border">
        <div className="space-y-0">
          <Skeleton className="h-10 w-full rounded-none" />
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-none border-t border-border/50" />
          ))}
        </div>
      </div>
    )
  }

  if (rows.length === 0) {
    return (
      <div className="flex items-center gap-3.5 bg-card px-4 py-5">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted/40 text-muted-foreground">
          <ShieldCheck className="size-4" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">
            {t('settings.security.noSessionSignals')}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="h-9 whitespace-nowrap text-xs font-medium text-muted-foreground">
                {t('settings.security.table.event')}
              </TableHead>
              <TableHead className="h-9 whitespace-nowrap text-xs font-medium text-muted-foreground">
                {t('settings.security.table.detail')}
              </TableHead>
              <TableHead className="h-9 whitespace-nowrap text-xs font-medium text-muted-foreground">
                {t('settings.security.table.ipAddress')}
              </TableHead>
              <TableHead className="hidden sm:table-cell h-9 whitespace-nowrap text-xs font-medium text-muted-foreground">
                {t('settings.security.table.device')}
              </TableHead>
              <TableHead className="h-9 whitespace-nowrap text-right text-xs font-medium text-muted-foreground">
                {t('settings.security.table.timestamp')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageSlice.map((log) => {
              const isTestFailed =
                (log.action_type === 'business_iacrm_tested' || log.action_type === 'own_iacrm_tested') &&
                log.metadata?.success === false
              const tone = isTestFailed ? 'danger' : (actionTypeTone[log.action_type] ?? 'neutral')
              const eventLabel = t(`settings.security.eventTypes.${log.action_type}`, {
                defaultValue: log.action_type.replace(/_/g, ' '),
              })
              const statusLabel = t(`settings.security.statusTypes.${log.action_type}`, {
                defaultValue:
                  tone === 'success'
                    ? t('common.success')
                    : tone === 'warning'
                      ? t('common.warning')
                      : tone === 'danger'
                        ? t('common.error')
                        : t('common.info'),
              })

              return (
                <TableRow key={log.id} className="group">
                  <TableCell className="py-2.5">
                    <div className="flex items-center gap-2.5">
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium capitalize',
                          actionToneClass(tone),
                        )}
                      >
                        {statusLabel}
                      </span>
                      <span className="text-sm text-foreground">{eventLabel}</span>
                    </div>
                  </TableCell>
                  <TableCell className="py-2.5 text-sm text-muted-foreground">
                    {t(`settings.security.descriptions.${log.action_type}`, {
                      defaultValue: log.description ?? '—',
                    })}
                  </TableCell>
                  <TableCell className="py-2.5 text-sm tabular-nums text-muted-foreground">
                    {log.ip_address ?? '—'}
                  </TableCell>
                  <TableCell className="hidden sm:table-cell py-2.5 text-sm text-muted-foreground">
                    {simplifyUserAgent(log.user_agent, {
                      unknownBrowser: t('settings.security.unknownBrowser'),
                      unknownDevice: t('settings.security.unknownDevice'),
                    })}
                  </TableCell>
                  <TableCell className="py-2.5 text-right text-sm tabular-nums text-muted-foreground">
                    {formatDateTime(log.created_at, i18n.language)}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
      <TablePaginationBar
        page={pageSafe}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size)
          setPage(1)
        }}
        pageSizeOptions={[5, 10, 25, 50]}
      />
    </div>
  )
}
